import _ from 'lodash';
import Decimal from 'decimal.js';
import moment from 'moment';
import { AP_SUFFIX, FX, M_SUFFIX } from '../constants';
import {
  PL_TREND_SWAP_LABEL_BY_SERVICE_ID,
  SWAP_DISPLAY_REALIZED,
  SWAP_DISPLAY_UNREALIZED,
} from '../constants/portfolio';

const roundToInt = (value) => Decimal(value).toDP(0, Decimal.ROUND_FLOOR).toNumber();

const getChartData = (items, { useUnrealizedSwap = false } = {}) => {
  const sumByDate = {};

  items
    .filter(({ pl, swapPl, accruedSwap }) => {
      const swapValue = useUnrealizedSwap ? accruedSwap : swapPl;
      return pl != null || swapValue !== 0;
    })
    .forEach((item) => {
      const swapValue = useUnrealizedSwap ? item.accruedSwap : item.swapPl;
      const dailyTotal = Decimal.add(item.pl ?? 0, swapValue ?? 0).toNumber();

      if (!(item.date in sumByDate)) sumByDate[item.date] = dailyTotal;
      else sumByDate[item.date] = Decimal.add(sumByDate[item.date], dailyTotal).toNumber();
    });

  const formatted = Object.entries(sumByDate).map(([time, value]) => ({ time, value: roundToInt(value) }));

  return [...formatted];
};

const changeInstrumentId = (instrumentId, instrumentList) => {
  if (instrumentId?.includes(M_SUFFIX)) {
    const modifiedInstrumentId = instrumentId.replace(M_SUFFIX, AP_SUFFIX);
    const originItem = instrumentList?.[instrumentId];
    const modifiedItem = instrumentList?.[modifiedInstrumentId];
    if (
      originItem &&
      modifiedItem &&
      originItem?.serviceId === modifiedItem?.serviceId &&
      originItem?.symbol === modifiedItem?.symbol
    ) {
      return modifiedInstrumentId;
    }
    return instrumentId;
  }
  return instrumentId;
};

const getInstrumentsPl = (items, { doSummaryInstrumentId = false, instrumentList }) => {
  const result = {};
  const orders = Object.keys(instrumentList); // APIのresponseがsortNo順となっている想定
  const order = (id) => orders.indexOf(id);

  items.forEach((item) => {
    const { pl = 0, swapPl = 0, accruedSwap = 0, openExecutionCount = 0, closeExecutionCount = 0 } = item;
    const key =
      doSummaryInstrumentId && instrumentList
        ? changeInstrumentId(item.instrumentId, instrumentList)
        : item.instrumentId;
    if (!(key in result))
      result[key] = {
        pl,
        realizedSwapPl: swapPl,
        unrealizedSwapPl: accruedSwap,
        openExecutionCount,
        closeExecutionCount,
      };
    else {
      const prev = result[key];
      result[key] = {
        pl: prev.pl + pl,
        realizedSwapPl: prev.realizedSwapPl + swapPl,
        unrealizedSwapPl: Decimal.add(prev.unrealizedSwapPl, accruedSwap).toNumber(),
        openExecutionCount: prev.openExecutionCount + openExecutionCount,
        closeExecutionCount: prev.closeExecutionCount + closeExecutionCount,
      };
    }
  });

  return Object.entries(result)
    .map(([instrumentId, summary]) => ({
      instrumentId,
      ...summary,
      unrealizedSwapPl: roundToInt(summary.unrealizedSwapPl ?? 0),
    }))
    .sort(({ instrumentId: a }, { instrumentId: b }) => order(a) - order(b));
};

const getTotalPl = (items) => {
  const initialValue = 0;
  return items.reduce((accumulator, currentItem) => accumulator + currentItem.pl, initialValue);
};

const getTotalRealizedSwapPl = (items) => {
  const initialValue = 0;
  return items.reduce((accumulator, currentItem) => accumulator + (currentItem.realizedSwapPl ?? 0), initialValue);
};

const getTotalUnrealizedSwapPl = (items) => {
  const initialValue = 0;
  const total = items.reduce(
    (accumulator, currentItem) => Decimal.add(accumulator, currentItem.accruedSwap ?? 0).toNumber(),
    initialValue,
  );
  return roundToInt(total);
};

const getNumberOfEntries = (items) => {
  const initialValue = 0;
  return items.reduce((accumulator, currentItem) => accumulator + currentItem.openExecutionCount, initialValue);
};

const getNumberOfExits = (items) => {
  const initialValue = 0;
  return items.reduce((accumulator, currentItem) => accumulator + currentItem.closeExecutionCount, initialValue);
};

const current = moment();
export const today = current.toDate();
export const defaultFromDate = moment().subtract(1, 'year').add(1, 'day').toDate();

const getMonthOptions = (input) => {
  const inputDate = moment(input, 'YYYYMMDD');
  const inputMonth = Number(inputDate.format('M'));

  return _.range(12).map((n) => {
    let year = inputDate.year();
    let month = inputMonth - n;

    if (month < 1) {
      month += 12;
      year -= 1;
    }

    const monthStr = month > 9 ? String(month) : `0${month}`;

    return { label: `${year}年${month}月`, value: `${year}-${monthStr}-` };
  });
};

export const isSameDate = (currentFrontDate, dateStr) => {
  // compare currentFrontDate(YYYYMMDD) vs 'dateStr(YYYY-MM-DD)
  return currentFrontDate && dateStr && currentFrontDate === dateStr.replaceAll('-', '');
};

export const getPortfolioPlTrendSwapLabel = ({ serviceId } = {}) =>
  PL_TREND_SWAP_LABEL_BY_SERVICE_ID[serviceId] ?? PL_TREND_SWAP_LABEL_BY_SERVICE_ID[FX];

export const getPortfolioPlTrendSwapDisplayOptions = ({ serviceId } = {}) => {
  const realizedSwapLabel = getPortfolioPlTrendSwapLabel({ serviceId });
  const unrealizedSwapLabel = realizedSwapLabel.replace('・分配', '');
  const pointLabel = serviceId === FX ? 'ポイント' : '';

  return [
    {
      value: SWAP_DISPLAY_REALIZED,
      label: `実現${realizedSwapLabel}`,
      description:
        `実現損益と決済や${realizedSwapLabel}振替により実現した${realizedSwapLabel}${pointLabel}` +
        'の合計を表示します。',
    },
    {
      value: SWAP_DISPLAY_UNREALIZED,
      label: `未実現${unrealizedSwapLabel}`,
      description:
        `実現損益と保有建玉に発生した${unrealizedSwapLabel}${pointLabel}を表示します。` +
        `${unrealizedSwapLabel}${pointLabel}は口座反映日に表示されます。`,
    },
  ];
};

export const getPortfolioPlTrendTooltipMessages = ({ selectedSwapDisplay, serviceId } = {}) => {
  const realizedSwapLabel = getPortfolioPlTrendSwapLabel({ serviceId });
  const unrealizedSwapLabel = realizedSwapLabel.replace('・分配', '');
  const pointLabel = serviceId === FX ? 'ポイント' : '';

  return {
    totalPlTooltipMessage:
      selectedSwapDisplay === SWAP_DISPLAY_REALIZED
        ? `実現損益と実現${realizedSwapLabel}の合計。`
        : `実現損益と未実現${unrealizedSwapLabel}の合計。実現${realizedSwapLabel}は含まない。`,
    realizedPlTooltipMessage: '取引日基準で計算した売買損益の合計。',
    swapTooltipMessage:
      selectedSwapDisplay === SWAP_DISPLAY_REALIZED
        ? `建玉の決済または${realizedSwapLabel}振替により実現した${realizedSwapLabel}${pointLabel}の合計。`
        : `保有建玉に発生した${unrealizedSwapLabel}${pointLabel}の合計。 `,
  };
};

const profitLossUtils = {
  getChartData,
  getInstrumentsPl,
  getPortfolioPlTrendSwapDisplayOptions,
  getPortfolioPlTrendTooltipMessages,
  getNumberOfEntries,
  getNumberOfExits,
  getTotalPl,
  getTotalRealizedSwapPl,
  getTotalUnrealizedSwapPl,
  getMonthOptions,
};

export default profitLossUtils;
